import { useMemo } from "react";
import { index, InternMap } from "d3";
import { ProductClass } from "../types/enums";
import {
  useLocationsMetadata,
  useProductsMetadata,
} from "./useMetadata";
import type { MetadataFetchState } from "../types/metadataState";
import type {
  ProductMetadatum,
  LocationMetadatum,
} from "../types/metadata";
import { locationMembersInclude } from "../types/metadata";
import type { ProductId } from "../types/branded";

export interface ProductLookups {
  /** Lookup product by productId */
  byId: InternMap<string, ProductMetadatum>;
  /** Lookup product by code (for 4-digit products only) */
  byCode: InternMap<string, ProductMetadatum>;
  /** Lookup products by section (1-digit parent) */
  bySection: InternMap<string, ProductMetadatum[]>;
  /** Get products by level */
  byLevel: {
    section: ProductMetadatum[];
    twoDigit: ProductMetadatum[];
    fourDigit: ProductMetadatum[];
    sixDigit: ProductMetadatum[];
  };
}

export function useProductLookups(
  productClass: ProductClass,
  options?: { servicesClass?: string },
): MetadataFetchState<ProductLookups> {
  const productsState = useProductsMetadata(productClass, options);
  const rawData = productsState.status === "success" ? productsState.data : undefined;

  const lookups = useMemo<ProductLookups | undefined>(() => {
    if (!rawData) return undefined;

    // ── Branding boundary ──────────────────────────────────────────
    // Product IDs in this lookup are scoped to `productClass`.
    // Callers that know the classification can cast keys to
    // ProductId<C> for compile-time safety against cross-classification mixing.

    // Flatten all products into a single array for global lookups
    const allProducts: ProductMetadatum[] = [
      ...(rawData.section || []),
      ...(rawData.twoDigit || []),
      ...(rawData.fourDigit || []),
      ...(rawData.sixDigit || []),
    ];

    // Create lookup by productId (works for all levels)
    const byId = index(
      allProducts,
      (d: ProductMetadatum) => d.productId,
    ) as InternMap<string, ProductMetadatum>;

    // Create lookup by code (for 4-digit products specifically)
    const fourDigitWithCodes = (rawData.fourDigit || []).filter(
      (d: ProductMetadatum) => d.code,
    );
    const byCode = index(
      fourDigitWithCodes,
      (d: ProductMetadatum) => d.code!,
    ) as InternMap<string, ProductMetadatum>;

    // Create lookup by section (top-level parent)
    const productsBySection = new InternMap<string, ProductMetadatum[]>();
    allProducts.forEach((product: ProductMetadatum) => {
      const sectionId =
        product.topParent?.productId || product.parent?.productId || product.productId;
      if (!productsBySection.has(sectionId)) {
        productsBySection.set(sectionId, []);
      }
      productsBySection.get(sectionId)!.push(product);
    });

    return {
      byId,
      byCode,
      bySection: productsBySection,
      byLevel: {
        section: rawData.section || [],
        twoDigit: rawData.twoDigit || [],
        fourDigit: rawData.fourDigit || [],
        sixDigit: rawData.sixDigit || [],
      },
    };
  }, [rawData]);

  if (productsState.status === "error") return { status: "error", error: productsState.error };
  if (productsState.status === "loading") return { status: "loading" };
  if (lookups !== undefined) return { status: "success", data: lookups };
  return { status: "idle" };
}

export interface LocationLookups {
  /** Lookup location by countryId or groupId */
  byId: InternMap<string | number, LocationMetadatum>;
  /** All countries */
  countries: LocationMetadatum[];
  /** All regions */
  regions: LocationMetadatum[];
  /** All subregions */
  subregions: LocationMetadatum[];
  /** All locations flattened */
  all: LocationMetadatum[];
}

export function useLocationLookups(): MetadataFetchState<LocationLookups> {
  const locationsState = useLocationsMetadata();
  const rawData = locationsState.status === "success" ? locationsState.data : undefined;

  const lookups = useMemo<LocationLookups | undefined>(() => {
    if (!rawData) return undefined;

    const { countries, regions, subregions } = rawData;

    // Flatten all locations
    const allLocations: LocationMetadatum[] = [
      ...countries,
      ...regions,
      ...subregions,
    ];

    // Create lookup by ID (handles both countryId and groupId)
    const byId = index(
      allLocations,
      (d: LocationMetadatum) =>
        d.countryId ?? d.groupId ?? "",
    ) as InternMap<string | number, LocationMetadatum>;

    return {
      byId,
      countries,
      regions,
      subregions,
      all: allLocations,
    };
  }, [rawData]);

  if (locationsState.status === "error") return { status: "error", error: locationsState.error };
  if (locationsState.status === "loading") return { status: "loading" };
  if (lookups !== undefined) return { status: "success", data: lookups };
  return { status: "idle" };
}

export function useCombinedLookups(
  productClass: ProductClass,
  options?: { servicesClass?: string },
) {
  const productLookups = useProductLookups(productClass, options);
  const locationLookups = useLocationLookups();

  const loading = productLookups.status === "loading" || locationLookups.status === "loading";
  const error = (productLookups.status === "error" ? productLookups.error : undefined) ||
    (locationLookups.status === "error" ? locationLookups.error : undefined);

  return {
    products: productLookups,
    locations: locationLookups,
    loading,
    error,
  };
}

export function findProductById(
  lookups: ProductLookups | undefined,
  productId: string | ProductId | undefined,
): ProductMetadatum | undefined {
  if (!lookups || !productId) return undefined;
  return lookups.byId.get(productId);
}

export function findProductByCode(
  lookups: ProductLookups | undefined,
  code: string | undefined,
): ProductMetadatum | undefined {
  if (!lookups || !code) return undefined;
  return lookups.byCode.get(code);
}

export function findLocationById(
  lookups: LocationLookups | undefined,
  locationId: string | number | undefined,
): LocationMetadatum | undefined {
  if (!lookups || locationId === undefined) return undefined;
  return lookups.byId.get(locationId);
}

export function getLocationName(
  lookups: LocationLookups | undefined,
  locationId: string | number | undefined,
): string | undefined {
  const location = findLocationById(lookups, locationId);
  if (!location) return undefined;
  if ("nameEn" in location) return location.nameEn ?? undefined;
  if ("groupName" in location) return location.groupName;
  return undefined;
}

/**
 * Get products for a specific section
 */
export function getProductsBySection(
  lookups: ProductLookups | undefined,
  sectionId: string | undefined,
): ProductMetadatum[] {
  if (!lookups || !sectionId) return [];
  return lookups.bySection.get(sectionId) || [];
}

export function enrichProductData(
  productId: string | ProductId,
  lookups: ProductLookups | undefined,
): {
  topLevelParent: string | undefined;
  nameEn: string | undefined;
  productCode: string | undefined;
  sectorId: string | undefined;
} {
  const product = lookups?.byId.get(productId);
  if (!product) {
    return {
      topLevelParent: undefined,
      nameEn: undefined,
      productCode: undefined,
      sectorId: undefined,
    };
  }

  return {
    topLevelParent: product.topParent?.productId,
    nameEn: product.nameShortEn ?? undefined,
    productCode: product.code ?? undefined,
    sectorId: product.topParent?.productId,
  };
}

export function getProductName(
  productId: string | ProductId | undefined,
  lookups: ProductLookups | undefined,
): string | undefined {
  if (!productId || !lookups) return undefined;
  const product = lookups.byId.get(productId);
  return product?.nameShortEn ?? product?.nameEn ?? undefined;
}

export function getProductSection(
  productId: string | ProductId | undefined,
  lookups: ProductLookups | undefined,
): string | undefined {
  if (!productId || !lookups) return undefined;
  const product = lookups.byId.get(productId);
  return product?.topParent?.productId;
}

export function getCountryRegion(
  countryId: number | undefined,
  lookups: LocationLookups | undefined,
): string | undefined {
  if (countryId === undefined || !lookups) return undefined;

  const region = lookups.regions.find((r: LocationMetadatum) =>
    locationMembersInclude(r.members, countryId),
  );

  return region?.groupId;
}

export function getCountrySubregion(
  countryId: number | undefined,
  lookups: LocationLookups | undefined,
): string | undefined {
  if (countryId === undefined || !lookups) return undefined;

  const subregion = lookups.subregions.find((sr: LocationMetadatum) =>
    locationMembersInclude(sr.members, countryId),
  );

  return subregion?.groupId;
}
